import React from 'react';
import AlignedPhotoFrame from '../AlignedPhotoFrame';
import { CardShell, BrandFooter, PhotoTag, scaleFor, ORANGE, HAIRLINE } from '../CardBits';

// Template A — Full-bleed Split.
// Portrait keeps a solid data bar under the photos. Square and wide dissolve it
// into an overlay scrim so the photos keep every pixel, and the delta becomes a
// pill on the divider.
//
// EXPORT SAFETY: no object-fit / clip-path / filter. See lib/photoRender.js.
export default function SplitCard({ model, W, H, form }) {
  const s = scaleFor(H);
  const solidBar = form === 'portrait';
  const barH = solidBar ? 300 * s : 0;
  const photoH = H - barH;
  const halfW = W / 2;

  const photo = (side, tone) => (
    <div style={{ position: 'relative', width: halfW, height: photoH }}>
      <AlignedPhotoFrame
        url={side.url}
        backdropUrl={side.backdropUrl}
        canonical={side.canonical}
        manual={side.manual}
        natural={side.natural}
        slotW={halfW}
        slotH={photoH}
      />
      <PhotoTag tone={tone} s={s} style={{ left: 20 * s, top: 20 * s }}>
        {tone === 'accent' ? model.labels.after : model.labels.before}
        {!solidBar && side.weightLabel ? ` · ${side.weightLabel}` : ''}
      </PhotoTag>
    </div>
  );

  return (
    <CardShell W={W} H={H} background="#000000">
      <div style={{ position: 'relative', display: 'flex', width: W, height: photoH }}>
        {photo(model.before, 'dark')}
        {photo(model.after, 'accent')}

        {/* Divider */}
        <div
          style={{
            position: 'absolute',
            left: halfW - 1.5 * s,
            top: 0,
            width: 3 * s,
            height: photoH,
            background: 'rgba(255,255,255,0.9)',
          }}
        />

        {/* Overlay chrome for square / wide */}
        {!solidBar && (
          <>
            <div
              style={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                marginLeft: -170 * s,
                marginTop: -50 * s,
                width: 340 * s,
                background: ORANGE,
                borderRadius: 999,
                padding: `${14 * s}px 0`,
                textAlign: 'center',
              }}
            >
              <div style={{ fontSize: 56 * s, fontWeight: 800, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                {model.hero.value}{model.hero.unit}
              </div>
              {model.hero.sub && (
                <div style={{ fontSize: 22 * s, letterSpacing: 3 * s, opacity: 0.85 }}>{model.hero.sub}</div>
              )}
            </div>

            <div
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: 0,
                height: 220 * s,
                background: 'linear-gradient(rgba(0,0,0,0), rgba(0,0,0,0.92))',
              }}
            />
            <div style={{ position: 'absolute', left: 30 * s, right: 30 * s, bottom: 26 * s }}>
              <BrandFooter handleLabel={model.handleLabel} s={s} bordered={false} />
            </div>
          </>
        )}
      </div>

      {/* Solid data bar for portrait */}
      {solidBar && (
        <div style={{ height: barH, background: '#0a0a0a', padding: `${26 * s}px ${30 * s}px ${20 * s}px` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
            <SideBlock side={model.before} s={s} align="left" />
            <div style={{ textAlign: 'center', paddingBottom: 6 * s }}>
              {model.hero.sub && (
                <div style={{ fontSize: 22 * s, letterSpacing: 3 * s, opacity: 0.45 }}>
                  {model.hero.sub.toUpperCase()}
                </div>
              )}
              <div style={{ fontSize: 72 * s, fontWeight: 800, color: ORANGE, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                {model.hero.value}
                <span style={{ fontSize: 30 * s }}>{model.hero.unit}</span>
              </div>
            </div>
            <SideBlock side={model.after} s={s} align="right" />
          </div>
          <div style={{ marginTop: 20 * s, borderTop: `${Math.max(1, 2 * s)}px solid ${HAIRLINE}`, paddingTop: 14 * s }}>
            <BrandFooter handleLabel={model.handleLabel} s={s} bordered={false} />
          </div>
        </div>
      )}
    </CardShell>
  );
}

function SideBlock({ side, s, align }) {
  return (
    <div style={{ textAlign: align }}>
      {side.dateLabel && (
        <div style={{ fontSize: 22 * s, letterSpacing: 3 * s, opacity: 0.45 }}>{side.dateLabel}</div>
      )}
      {side.weightLabel && (
        <div style={{ fontSize: 56 * s, fontWeight: 800, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
          {side.weightLabel}
        </div>
      )}
      {side.bodyFatLabel && (
        <div style={{ fontSize: 26 * s, opacity: 0.5 }}>{side.bodyFatLabel}</div>
      )}
    </div>
  );
}
